import { useMemo, useState } from "react";
import {
  Activity,
  ListFilter,
  LockKeyhole,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";

import { ManagedSigningPanel } from "./ManagedSigningPanel";
import { useManagedSigningExperienceCopy } from "./managedSigningExperienceCatalog";
import "../../styles/keys-trust-experience.css";
import { EvidenceWrapAuthorityPanel } from "./EvidenceWrapAuthorityPanel";
import { CollectorLabSignerReadiness } from "./CollectorLabSignerReadiness";
import { KeysTrustPage } from "./KeysTrustPage";
import { TufHealthPanel } from "./TufHealthPanel";
import { CollectorPublishingSetup } from "./CollectorPublishingSetup";
import { TufSigningPreparationPanel } from "./TufSigningPreparationPanel";
import {
  useKeysTrustManagedCopy,
  type KeysTrustManagedMessageKey,
} from "./keysTrustManagedCatalog";

import "../../styles/keys-trust.css";

type KeysTrustTab = "overview" | "inventory" | "signing" | "evidence" | "tuf";

type TabDefinition = {
  id: KeysTrustTab;
  labelKey: KeysTrustManagedMessageKey | "releaseSigning" | "evidenceWrapping";
  icon: LucideIcon;
};

const TABS: TabDefinition[] = [
  { id: "overview", labelKey: "overview", icon: ShieldCheck },
  { id: "inventory", labelKey: "inventory", icon: ListFilter },
  { id: "signing", labelKey: "releaseSigning", icon: ShieldCheck },
  { id: "evidence", labelKey: "evidenceWrapping", icon: LockKeyhole },
  { id: "tuf", labelKey: "tufHealth", icon: Activity },
];

export function KeysTrustManagedPage({
  onSessionUnavailable,
}: {
  onBack?: () => void;
  onSessionUnavailable?: () => void;
}) {
  const { t } = useKeysTrustManagedCopy();
  const c=useManagedSigningExperienceCopy();
  const [tab, setTab] = useState<KeysTrustTab>("overview");
  const panelCallbacks = onSessionUnavailable ? { onSessionUnavailable } : {};
  const activeTab = useMemo(() => TABS.find((item) => item.id === tab)!, [tab]);

  return (
    <div className="keys-trust-workspace">
      <header className="admin-v12-page-heading keys-trust-heading">
        <div>
          <p className="admin-v12-eyebrow">{t("eyebrow")}</p>
          <h1>{t("title")}</h1>
          <p>{t("description")}</p>
        </div>
        <div className="keys-trust-heading-boundary">
          <LockKeyhole size={17} />
          <span><strong>{t("custodyTitle")}</strong><small>{t("custodyBody")}</small></span>
        </div>
      </header>

      <nav className="admin-v12-tabs keys-trust-tabs" role="tablist" aria-label={t("sectionsAria")}>
        {TABS.map((item) => {
          const Icon = item.icon;
          return <button
            id={`keys-trust-tab-${item.id}`}
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            aria-controls="keys-trust-tab-panel"
            onClick={() => setTab(item.id)}
          >
            <Icon size={15} />{item.labelKey === "releaseSigning" || item.labelKey === "evidenceWrapping" ? c(item.labelKey) : t(item.labelKey)}
          </button>;
        })}
      </nav>

      <section
        id="keys-trust-tab-panel"
        className="keys-trust-tab-panel"
        role="tabpanel"
        aria-labelledby={`keys-trust-tab-${activeTab.id}`}
      >
        {tab === "overview" && <>
          <KeysTrustPage
            view="overview"
            {...panelCallbacks}
            onOpenInventory={() => setTab("inventory")}
            onOpenTuf={() => setTab("tuf")}
          />
          <section className="keys-purpose-navigation" aria-label={t("sectionsAria")}>{TABS.filter(item=>["signing","evidence","tuf"].includes(item.id)).map(item=><button type="button" key={item.id} onClick={()=>setTab(item.id)}><item.icon size={19}/><strong>{item.labelKey === "releaseSigning" || item.labelKey === "evidenceWrapping" ? c(item.labelKey) : t(item.labelKey)}</strong><span aria-hidden="true">→</span></button>)}</section>
          <details className="keys-lab-readiness"><summary>{t("familyCollectorRelease")}</summary><CollectorLabSignerReadiness /></details>
        </>}

        {tab === "signing" && <ManagedSigningPanel {...panelCallbacks} />}
        {tab === "evidence" && <EvidenceWrapAuthorityPanel {...panelCallbacks} />}
        {tab === "inventory" && <KeysTrustPage view="inventory" {...panelCallbacks} />}
        {tab === "tuf" && <><CollectorPublishingSetup />
          <details><summary>{t("tufTechnicalDetails")}</summary>
            <TufHealthPanel {...panelCallbacks} /><TufSigningPreparationPanel {...panelCallbacks} />
          </details>
        </>}
      </section>
    </div>
  );
}
